import { useEffect, useRef } from "react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { useRelay } from "../lib/relayContext.tsx";

interface Props {
  machineId: string;
  terminalId: string;
  visible: boolean;
  onExit: (code: number | null) => void;
}

/**
 * xterm.js ⇄ relay ⇄ node-pty. Attaches (replaying scrollback) on mount and again after a reconnect, so the
 * shell survives browser disconnects. Output events received before attach completes are buffered.
 */
export function TerminalView({ machineId, terminalId, visible, onExit }: Props) {
  const relay = useRelay();
  const host = useRef<HTMLDivElement>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;

  useEffect(() => {
    if (!host.current) return;
    const term = new Terminal({
      cursorBlink: true,
      fontSize: 13,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      theme: { background: "#0f1115" },
      scrollback: 5000,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(host.current);
    fitRef.current = fit;

    let attached = false;
    let buffered: string[] = [];
    const attach = () => {
      attached = false;
      buffered = [];
      relay
        .machine(machineId, "terminal/terminal.attach", { terminalId })
        .then((r) => {
          term.reset();
          term.write(r.scrollback);
          for (const d of buffered) term.write(d);
          attached = true;
          if (r.terminal.exited) onExitRef.current(r.terminal.exitCode);
          fit.fit();
          relay.notify(machineId, "terminal/terminal.resize", {
            terminalId,
            cols: term.cols,
            rows: term.rows,
          });
        })
        .catch((e: unknown) => term.write(`\r\n\x1b[31m[racler] ${(e as Error).message}\x1b[0m\r\n`));
    };

    const offEvents = relay.on((msg) => {
      if (msg.machineId !== machineId || msg.channel !== "terminal") return;
      const p = msg.payload as { terminalId: string; data?: string; exitCode?: number | null };
      if (p.terminalId !== terminalId) return;
      if (msg.type === "terminal.output" && p.data !== undefined) {
        if (attached) term.write(p.data);
        else buffered.push(p.data);
      } else if (msg.type === "terminal.exit") {
        term.write("\r\n\x1b[2m[process exited]\x1b[0m\r\n");
        onExitRef.current(p.exitCode ?? null);
      }
    });
    const offReconnect = relay.onReconnect(() => {
      relay
        .server("system/machine.subscribe", { machineId })
        .then(attach)
        .catch(() => undefined);
    });
    const input = term.onData((data) =>
      relay.notify(machineId, "terminal/terminal.input", { terminalId, data }),
    );
    const resize = term.onResize(({ cols, rows }) =>
      relay.notify(machineId, "terminal/terminal.resize", { terminalId, cols, rows }),
    );
    const observer = new ResizeObserver(() => {
      // A collapsed bottom panel has no size: never fit (that would shrink the pty to a few rows).
      const el = host.current;
      if (el?.offsetParent && el.clientHeight > 20 && el.clientWidth > 20) fit.fit();
    });
    observer.observe(host.current);
    attach();

    return () => {
      observer.disconnect();
      offEvents();
      offReconnect();
      input.dispose();
      resize.dispose();
      term.dispose();
    };
  }, [relay, machineId, terminalId]);

  useEffect(() => {
    if (visible) requestAnimationFrame(() => fitRef.current?.fit());
  }, [visible]);

  return (
    <div
      className="terminal-host"
      ref={host}
      style={{ display: visible ? "block" : "none" }}
      data-testid="terminal"
    />
  );
}
